import { apiGet, APIURL, cancelSelectEvent, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseCryptoAirdrops {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private currentTab: 'ENDED' | 'ONGOING' | 'UPCOMING' = 'UPCOMING';

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }


  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;

    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    this.attachStyles();
    await this.setupChart();
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    this.config.onReady?.();
    cancelSelectEvent(this.shadowRoot);
  }

  private handleTabClick = async (event: Event): Promise<void> => {
    const clicked = (event.currentTarget as HTMLElement) || ((event.target as HTMLElement).closest('.btItem') as HTMLElement | null);
    if (!clicked) return;

    const tabType = clicked.getAttribute('data-tab') as 'ENDED' | 'ONGOING' | 'UPCOMING' | null;
    if (tabType && this.currentTab !== tabType) {
      this.currentTab = tabType;
      await this.setupChart();

      const tabs = this.shadowRoot.querySelectorAll('.btItem');
      tabs.forEach(tab => {
        (tab as HTMLElement).classList.remove('active');
        if ((tab as HTMLElement).getAttribute('data-tab') === tabType) {
          (tab as HTMLElement).classList.add('active');
        }
      });
    }
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title crypto">${this.getTitleIconSvg()} Airdrops</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<i class="pi pi-ellipsis-v"></i></div>
        </div>

        <div class="inBody">
          <div class="optionsRow" data-testing-id="options-row">
            <div class="item"></div>
            <span></span>
            <div class="item tabsDv padHt" data-testing-id="tabs-container">
              <button class="btItem ${this.currentTab === 'ENDED' ? 'active' : ''}" data-tab="ENDED" data-testing-id="tab-ended">ENDED</button>
              <button class="btItem ${this.currentTab === 'ONGOING' ? 'active' : ''}" data-tab="ONGOING" data-testing-id="tab-ongoing">ONGOING</button>
              <button class="btItem ${this.currentTab === 'UPCOMING' ? 'active' : ''}" data-tab="UPCOMING" data-testing-id="tab-upcoming">UPCOMING</button>
            </div>
          </div>
          <div class="chartDv" id="eventsContainer" style="height: 100%; padding: 0; margin: 0;" data-testing-id="events-container"></div>
        </div>
      </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);

    const tabButtons = this.shadowRoot.querySelectorAll('.btItem');
    tabButtons.forEach(btn => btn.addEventListener('click', this.handleTabClick));

    this.setupChart();
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private async setupChart(): Promise<void> {
    try {
      const response = await apiGet({
        method: 'GET',
        url: `${APIURL}/v1/crypto/crypto_airdrop?limit=10&offset=0&type=${this.currentTab}`,
        body: { limit: 10, offset: 0, type: this.currentTab },
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error("Failed to fetch data");
      this.currentData = await response.json();
      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error("Error fetching chart data:", err);
    }
  }

  private trendingCoins(): void {
    if (!this.currentData?.data) return;
    let filtered = this.currentData.data;
    this.renderTrendingCoins(filtered);
  }

  private renderTrendingCoins(eventsData: any): void {
    const container = this.shadowRoot.querySelector('#eventsContainer');
    if (!container) return;

    if (!eventsData || eventsData.docs?.length === 0) {
      container.innerHTML = getNoDataHTML();
      return;
    }
      const hasData = !!eventsData?.docs?.length;

    let html = `
      <div class="eventsMain cryptoAirDropsMain"
       ${hasData ? 'data-testing-id="airdrop-table"' : ''}>
    <div class="eventsMain cryptoAirDropsMain">
      <div class="item header">
        <p>Project</p>
        <p>Coin</p>
        <p>Start Date</p>
        <p>End Date</p>
        <p>Total Prize</p>
        <p>Winner Count</p>
        <p>Link</p>
      </div>
    `;

    for (let idx = 0; idx < eventsData.docs.length; idx++) {
      const item = eventsData.docs[idx];
      const logoUrl = `https://media.traderverse.io/assets/crypto/${item.coin.symbol.toUpperCase().replace('USD', '')}_CRYPTO.svg`;
      const fallbackUrl = `https://cdn-images.traderverse.io/crypto_dummy.svg`;
      const safeId = `${item.coin.symbol || 'unknown'}-${idx}`.toLowerCase().replace(/[^a-z0-9-]/g, '-');

      html += `
      <div class="item body" 
           data-testing-id="airdrop-row-${idx}" 
           data-index="${idx}"
           data-tab="${this.currentTab}"
           data-project="${item.project_name || ''}"
           data-coin-symbol="${item.coin.symbol || ''}"
           data-coin-name="${item.coin.name || ''}"
           data-start-date="${item.start_date || ''}"
           data-end-date="${item.end_date || ''}"
           data-total-prize="${item.total_prize || ''}"
           data-winner-count="${item.winner_count || ''}">
        <p data-testing-id="cell-project"><span class="ellipsis">${item.project_name}</span></p>
        <p data-testing-id="cell-coin">
          <img 
            src="${logoUrl}" 
            width="20" 
            height="14" 
            style="vertical-align:middle;" 
            onerror="this.onerror=null;this.src='${fallbackUrl}';"
            data-testing-id="coin-img"
          /> ${item.coin.symbol} - ${item.coin.name}
        </p>
        <p data-testing-id="cell-start-date">${item.start_date ? new Date(item.start_date).toLocaleDateString() : 'N/A'}</p>
        <p data-testing-id="cell-end-date">${item.end_date ? new Date(item.end_date).toLocaleDateString() : 'N/A'}</p>
        <p data-testing-id="cell-total-prize">${item.total_prize}</p>
        <p data-testing-id="cell-winner-count">${item.winner_count}</p>
        <p class="stat" data-testing-id="cell-link"><a href="${item.link}" target="_blank" data-testing-id="view-link">View</a></p>
      </div>
    `;
    }

    html += `</div>`;
    container.innerHTML = html;
  }

  private updateDisplay(): void {
    if (!this.currentData) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.trendingCoins();
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.config.symbol = symbol;
    this.setupChart();
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }

  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }

    const tabContainer = this.shadowRoot.querySelector('.switch');
    if (tabContainer) {
      tabContainer.removeEventListener('click', this.handleTabClick);
    }

    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M15.15 20.3C12.3125 20.3 10 17.9875 10 15.15C10 12.3125 12.3125 10 15.15 10C17.9875 10 20.3 12.3125 20.3 15.15C20.3 17.9875 18 20.3 15.15 20.3ZM15.15 11.8625C13.3375 11.8625 11.875 13.3375 11.875 15.1375C11.875 16.9375 13.35 18.4125 15.15 18.4125C16.95 18.4125 18.425 16.9375 18.425 15.1375C18.425 13.3375 16.9625 11.8625 15.15 11.8625Z" fill="currentColor"/> <path d="M15 28.4375C7.5875 28.4375 1.5625 22.4125 1.5625 15C1.5625 7.5875 7.5875 1.5625 15 1.5625C22.4125 1.5625 28.4375 7.5875 28.4375 15C28.4375 22.4125 22.4125 28.4375 15 28.4375ZM15 3.4375C8.625 3.4375 3.4375 8.625 3.4375 15C3.4375 21.375 8.625 26.5625 15 26.5625C21.375 26.5625 26.5625 21.375 26.5625 15C26.5625 8.625 21.375 3.4375 15 3.4375Z" fill="currentColor"/> <path d="M15.0625 22C14.5158 22 14.0625 21.5192 14.0625 20.9394V9.06061C14.0625 8.48081 14.5158 8 15.0625 8C15.6092 8 16.0625 8.48081 16.0625 9.06061V20.9394C16.0625 21.5192 15.6092 22 15.0625 22Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseCryptoAirdrops: typeof TraderverseCryptoAirdrops; } }
if (typeof window !== 'undefined') { window.TraderverseCryptoAirdrops = TraderverseCryptoAirdrops; }